import React, { useEffect, useRef, useState } from 'react';
import {
  Field, useField, useForm, useFormState,
} from 'react-final-form';
import {
  Box, Button, Divider, FormControl, FormErrorMessage, Input, Text, useDisclosure,
} from '@chakra-ui/react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { ValidationErrors } from 'final-form';
import { NIL } from 'uuid';
import MarkdownTextInput from './MarkdownTextInput';
import DateInputControl from './DateInput';
import TextInputControl from './TextInput';
import TrackingUrlField from './TrackingUrlField'; // item 1310
import useWorkOrder from '../../hooks/useWorkOrder'; // item 1315 — 'created from' caption
import deferFormChanges from './deferFormChanges'; // item 1314
import FieldLabel from './FieldLabel';
import CurrencyInputControl from './CurrencyInput';
import SelectInputControl from './SelectInput';
import FormRow from './FormRow';
import ResponsiveStack from '../ResponsiveStack';
import DocumentFormScaffold from './DocumentFormScaffold';
import ServiceAddressSection from './ServiceAddressSection';
import ProjectSelectField from './ProjectSelectField';
import ViewPdfButton from './ViewPdfButton';
import SendDocumentButton from './SendDocumentButton';
import ReceivedPoAttachments from '../ReceivedPoAttachments';
import ClientPoUploadModal from '../ClientPoUploadModal';
import PurchaseOrderConvertModal from '../PurchaseOrderConvertModal';
import IconPurchaseOrders from '../../assets/Icon-PurchaseOrders.svg';
import IconPurchaseOrdersWhite from '../../assets/Icon-PurchaseOrders-White.svg';
import usePurchaseOrder from '../../hooks/usePurchaseOrder';
import { PurchaseOrder, PurchaseOrderItem, PurchaseOrderStatus } from '../../models/purchaseOrder';
import PurchaseOrderItemsFormElement from '../PurchaseOrderItemsFormElement';
import useContacts from '../../hooks/useContacts';
import { ContactDto, CustomerStaffMember } from '../../models/contactInfo';
import useUsers from '../../hooks/useUsers';
import { UserDto } from '../../models/user';
import useProjects from '../../hooks/useProjects';
import useContact from '../../hooks/useContact';
import { Address } from '../../models/address';

const addrHasContent = (a?: Address): boolean => !!a && !!(a.street || a.city || a.state || a.zip);

// dart (2026-07-14) — when adding a PO inside a project, prefill the Shipping Address from
// the project's customer (visible + editable; a drop-ship can be overridden via Places). Only
// on a NEW PO, and never clobbering a ship-to name the user typed themselves.
const PoCustomerPrefill: React.FC<{ isNew: boolean }> = ({ isNew }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const v = values as PurchaseOrder;
  const { projectId } = v;
  const { data: projects } = useProjects();
  const proj = projects?.index?.find((p) => p.id === projectId);
  // the lightweight projects/contacts lists carry no address, so fetch the customer's detail
  const { data: customer } = useContact({ id: proj?.customerId || NIL });
  const lastFilled = useRef<string | null>(null);
  useEffect(() => {
    if (!isNew || !customer || customer.id === NIL) return undefined;
    // don't overwrite a name the user typed — only fill when empty or still our own last fill
    if (v.serviceName && v.serviceName !== lastFilled.current) return undefined;
    const addr = addrHasContent(customer.shippingAddress) ? customer.shippingAddress : customer.serviceAddress;
    lastFilled.current = customer.name;
    // item 1314 — deferred one tick so no field swallows it as its 'first notification'
    return deferFormChanges(form, () => {
      form.change('serviceName', customer.name);
      // item 795 (Chad) — set each subfield individually, NOT the whole serviceAddress object:
      // final-form doesn't reliably repaint the nested <Field name="serviceAddress.street">
      // inputs when the parent object is replaced wholesale (the address filled but showed blank
      // until you clicked a cell). Mirrors the Places-autocomplete handler below.
      form.change('serviceAddress.street', addr?.street || '');
      form.change('serviceAddress.unit', addr?.unit || '');
      form.change('serviceAddress.city', addr?.city || '');
      form.change('serviceAddress.state', addr?.state || '');
      form.change('serviceAddress.zip', addr?.zip || '');
      form.change('serviceAddress.country', addr?.country || '');
      form.change('servicePhone', customer.phone || '');
      form.change('serviceEmail', customer.email || '');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, isNew]);
  return null;
};

// item 802 (Chad) — Shipping Instructions: a datalist-backed input — the common methods are
// suggestions in the dropdown, but you can TYPE ANY value (not limited to the preset list).
// Required. Drives the PDF ship banner (slate for Ground, red for anything else).
const SHIP_PRESETS = ['Ground', 'Two-Day', 'Overnight', 'Next-Day AM', 'Freight', 'Will Call / Pickup'];
const ShippingInstructionsField: React.FC = () => {
  const { input, meta } = useField('shippingInstructions');
  const invalid = !!meta.error && meta.touched;
  return (
    <FormControl isRequired isInvalid={invalid}>
      <FieldLabel htmlFor="shippingInstructions">Shipping Instructions</FieldLabel>
      <Input
        {...input}
        id="shippingInstructions"
        list="ship-methods"
        placeholder="Ground, Two-Day, Overnight…"
      />
      <datalist id="ship-methods">
        {SHIP_PRESETS.map((m) => <option key={m} value={m}>{m}</option>)}
      </datalist>
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

// item 800 (Chad) — Requested By = a CRM staff dropdown from the LIVE selected project's
// customer roster (works on a brand-new PO, before any save). Required — it's the wizard's
// client contact, whose email auto-fills the "order placed" confirmation. Free-text fallback
// only when that customer has no staff entered yet.
const RequestedByField: React.FC = () => {
  const { values } = useFormState({ subscription: { values: true } });
  const { projectId } = values as PurchaseOrder;
  const { data: projects } = useProjects();
  const proj = projects?.index?.find((p) => p.id === projectId);
  const { data: customer } = useContact({ id: proj?.customerId || NIL });
  const staff = customer?.staff ?? [];
  if (staff.length > 0) {
    return (
      <SelectInputControl name="requestedBy" label="Requested By" isRequired>
        <option value="">—</option>
        {staff.map((st: CustomerStaffMember) => (
          <option key={st.id} value={st.name}>{st.name}</option>
        ))}
      </SelectInputControl>
    );
  }
  return (
    <TextInputControl
      name="requestedBy"
      label="Requested By"
      isRequired
      helperText="Add staff to this customer in the CRM to pick the contact from a list."
    />
  );
};

const validate = (values: PurchaseOrder): ValidationErrors => {
  const errors: ValidationErrors = {};
  // item 465 — the Service Address name is required
  if (!values.serviceName) {
    errors.serviceName = 'Required';
  }
  // number is optional — left blank, the backend auto-generates it (item 101)
  if (!values.date) {
    errors.date = 'Required';
  }
  // every document must belong to a project (Chad, 2026-06-22)
  if (!values.projectId) {
    errors.projectId = 'Required';
  }
  // item 798 (Chad) — shipping method is required (drives the PDF ship stamp)
  if (!values.shippingInstructions) {
    (errors as Record<string, string>).shippingInstructions = 'Required';
  }
  // item 800 (Chad) — Requested By is required (the wizard's client contact)
  if (!values.requestedBy) {
    (errors as Record<string, string>).requestedBy = 'Required';
  }
  return errors;
};

const PurchaseOrderForm: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const {
    tryUpdate, data: purchaseOrder, status, trySetStatus,
  } = usePurchaseOrder({ id });
  // item 1315 — a PO cut from a Sales/Work Order names its source (PurchaseOrder.workOrderId)
  const { data: sourceOrder } = useWorkOrder({ id: purchaseOrder?.workOrderId || NIL });
  const { data: contacts, status: contactsStatus } = useContacts();
  const { data: users, status: usersStatus } = useUsers();

  // item 797 (Chad) — after the first save the PO lands here with a promptClientPo nav flag;
  // open the "book the client's PO" modal once (we already have their PO in hand).
  const location = useLocation<{ promptClientPo?: boolean }>();
  const history = useHistory();
  const poModal = useDisclosure();
  const convertModal = useDisclosure(); // item 813 — convert PO → SO/WO from the form
  const promptedRef = useRef(false);
  useEffect(() => {
    if (location.state?.promptClientPo && id !== NIL && !promptedRef.current) {
      promptedRef.current = true;
      poModal.onOpen();
      // clear the flag so a refresh / back-forward doesn't re-open it
      history.replace(`/PurchaseOrder/${id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, id]);

  // item 799 (Chad) — the guided PO ordering flow: upload client PO → Next → send PO to the
  // manufacturer (full email grid) → send the client an "order placed" confirmation → done.
  // Driven by autoOpenSignal counters on two hidden SendDocumentButtons.
  const [vendorSendSignal, setVendorSendSignal] = useState(0);
  const [clientSendSignal, setClientSendSignal] = useState(0);
  const poProjects = useProjects();
  const poProject = poProjects.data?.index?.find((pr) => pr.id === purchaseOrder?.projectId);
  const { data: poCustomer } = useContact({ id: poProject?.customerId || NIL });
  // the client confirmation goes to the selected "Requested By" staffer's email (item 800),
  // else the ship-to email, else the customer's main email.
  const requestedStaff = (poCustomer?.staff ?? []).find((st: CustomerStaffMember) => st.name === purchaseOrder?.requestedBy);
  const clientConfirmTo = requestedStaff?.email || purchaseOrder?.serviceEmail || poCustomer?.email || '';
  const poLabel = purchaseOrder?.purchaseOrderNumber ?? '';
  // item 1474 (Chad) — the client notice speaks in the CLIENT's PO number (the authorization they gave
  // us: the PO's own Client PO, else the source SO/WO's customer PO), never our vendor PO number.
  const clientPo = (purchaseOrder?.clientPoNumber || (sourceOrder as any)?.customerPoNumber || '').trim();

  const infoSection = (
    <>
      {/* dart — prefill the ship-to from the project's customer on a NEW PO (visible + editable) */}
      <PoCustomerPrefill isNew={id === NIL} />
      {/* item 672 — the PO ship-to is the Shipping Address: defaults from the project customer,
          carries the client's PO number, and supports Google Places lookup for drop-ships. */}
      {/* item 784 (Chad) — ship-to assumes the project's client (PoCustomerPrefill above);
          the picker drops any OTHER customer's address in for drop-ships. */}
      <ServiceAddressSection
        nameField="serviceName"
        heading="Shipping Address"
        poNumberField="clientPoNumber"
        customerPicker
        subHeading="Assumed to be the project's client. Pick any customer to drop-ship, or type a one-off recipient."
      />
      {/* item 257 / 793 (Chad) — the CLIENT'S original PO lives ON this purchase order (we
          receive their PO, cut our own to the manufacturer, and keep theirs as the reference).
          Moved up from the bottom + given an unsaved-state hint — it was invisible on ADD. */}
      {id !== NIL ? <ReceivedPoAttachments docId={id} /> : (
        <Box borderWidth="1px" borderRadius="md" p={3} mt={3} opacity={0.75}>
          <Text fontSize="sm" fontWeight="semibold">Received PO</Text>
          <Text fontSize="xs" mt={1}>
            Save this purchase order first — you&apos;ll stay right here and the Upload button
            appears, ready for the client&apos;s original PO (the stored reference for this order).
          </Text>
        </Box>
      )}
      {/* item 1574 — the customer-signed hardware delivery document (rides to the SO/WO + invoice) */}
      {id !== NIL && <ReceivedPoAttachments docId={id} kind="delivery" />}
      {sourceOrder?.workOrderNumber && (
        <Text fontSize="sm" mt={3} opacity={0.8}>
          {`Created from ${sourceOrder.isSalesOrder ? 'Sales Order' : 'Work Order'} `}
          <Text as="span" fontFamily="mono" fontWeight="semibold">{sourceOrder.workOrderNumber}</Text>
          {' — hardware at cost; the order keeps its group pricing.'}
        </Text>
      )}
      <Divider my={4} />
      {/* item 1310 (Chad) — shipment tracking travels PO → Sales Order → Invoice */}
      <FormRow heading="Shipment Tracking" subHeading="Optional — the carrier link for this order">
        <TrackingUrlField docLabel="purchase order" carriesTo="carries to the Sales Order and the invoice" />
      </FormRow>
      <Divider my={4} />
      <FormRow
        heading="Basic Information"
        subHeading="Information that will be the main identifiers for the purchase order"
      >
        {/* item 254f — lead with the important/required identifiers (Vendor, Date,
            Number); "Requested By" was a lone giant full-width field above the cramped
            required Vendor — demoted to the secondary row. */}
        <ResponsiveStack>
          <SelectInputControl name="vendorId" label="Vendor" isRequired>
            {/* item 1313 (Chad: "the vendor list on a PO should only be vendors now") — customers no longer clutter the Vendor pick-list */}
            {contactsStatus === 'success' && contacts && contacts.index
              .filter((c: ContactDto) => c.isVendor)
              .map((vendor: ContactDto) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
          </SelectInputControl>
          <DateInputControl
            name="date"
            label="Date"
            isRequired
          />
          {/* item 803 (Chad) — shipping method rides the top row (after Date, before PO Number) */}
          <ShippingInstructionsField />
          <TextInputControl
            name="purchaseOrderNumber"
            label="PO Number"
            isRequired={false}
            placeholder="Uses your standardized numbering — fill in to override with a custom number"
          />
        </ResponsiveStack>
        <ResponsiveStack>
          <RequestedByField />
          {/* item 1442 — a PO has no sales rep by nature: optional, and the server now accepts null */}
          <SelectInputControl name="salesRepId" label="Sales Rep (optional)">
            {usersStatus === 'success' && users
              // eslint-disable-next-line max-len
              && users.index.map((user: UserDto) => <option key={user.id} value={user.id}>{user.name.toUpperCase()}</option>)}
          </SelectInputControl>
        </ResponsiveStack>
        <ProjectSelectField name="projectId" label="Project" />
      </FormRow>

      <Divider my={4} />
      <FormRow
        heading="Internal Notes"
        subHeading="Internal Notes Of The Work"
      >
        <Field<string>
          name="internalNotes"
          component={MarkdownTextInput}
          label="Internal Notes"
        />
      </FormRow>
    </>
  );

  const afterItems = (
    <>
      <Divider my={4} />
      <FormRow
        heading="Global Discount"
        subHeading="Amount and the reason of any discount"
      >
        <ResponsiveStack>
          <CurrencyInputControl
            name="discount"
            label="Amount"
            isRequired={false}
          />
          <TextInputControl
            name="discountReason"
            label="Reason"
            isRequired={false}
          />
        </ResponsiveStack>
      </FormRow>
    </>
  );

  const scaffold = (
    <DocumentFormScaffold<PurchaseOrder, PurchaseOrderItem>
      config={{
        title: 'PURCHASE ORDER',
        saveLabel: 'Save Purchase Order',
        icon: IconPurchaseOrders,
        iconWhite: IconPurchaseOrdersWhite,
        doc: purchaseOrder,
        status,
        defaultCategory: 'Equipment', // item 288 — PO is a hardware/materials doc
        promptClientPoOnCreate: true, // item 797 — prompt to book the client's PO after create
        tryUpdate,
        validate,
        getItems: (doc) => doc.purchaseOrderItems,
        getCategoryNames: (doc) => doc.itemCategories,
        applyScaffoldState: (values, state) => {
          values.itemCategories = state.categoryNames;
          values.purchaseOrderItems = state.items;
          values.id = state.id;
        },
        ItemsElement: PurchaseOrderItemsFormElement,
        infoSection,
        afterItems,
        footerActions: id !== NIL ? (
          <>
            <ViewPdfButton path={`PurchaseOrder/${id}/pdf`} preview />
            <ViewPdfButton path={`PurchaseOrder/${id}/pdf`} />
            {/* item 813 (Chad) — convert PO → Sales Order / Work Order from the form (same
                shared modal as the ledger) */}
            <Button size="sm" variant="ghost" onClick={convertModal.onOpen}>Convert</Button>
            {/* item 111: email the PO to the vendor (+ CC). item 576 — SAME flow as the grid's
                "Send to vendor": copy comes from the server rule, and a successful send marks
                the PO Ordered (the footer previously skipped that status flip) */}
            <SendDocumentButton
              documentType="PurchaseOrder"
              documentId={id}
              documentLabel={purchaseOrder?.purchaseOrderNumber}
              onSent={() => trySetStatus(id, PurchaseOrderStatus.NewEntered)}
            />
          </>
        ) : undefined,
      }}
    />
  );
  return (
    <>
      {scaffold}
      <ClientPoUploadModal
        isOpen={poModal.isOpen}
        onClose={poModal.onClose}
        docId={id}
        previewPdfPath={id !== NIL ? `PurchaseOrder/${id}/pdf` : undefined}
        onNext={() => { poModal.onClose(); setVendorSendSignal((n) => n + 1); }}
      />
      {id !== NIL && (
        <PurchaseOrderConvertModal
          isOpen={convertModal.isOpen}
          onClose={convertModal.onClose}
          purchaseOrderId={id}
        />
      )}
      {/* item 799 — step 2: send the PO to the MANUFACTURER (full email grid; routes to the
          vendor orders email). On sent, advance to the client confirmation. */}
      {id !== NIL && (
        <SendDocumentButton
          documentType="PurchaseOrder"
          documentId={id}
          documentLabel={poLabel}
          hideTrigger
          autoOpenSignal={vendorSendSignal}
          onSent={() => { trySetStatus(id, PurchaseOrderStatus.NewEntered); setClientSendSignal((n) => n + 1); }}
        />
      )}
      {/* item 799 — step 3: tell the CLIENT the order was placed. NO attachment (the vendor PO
          shows distributor costs); To pre-fills from the Requested-By staffer (item 800).
          item 1474 (Chad) — the CRM picker lists the CUSTOMER's people (was the vendor's), and the
          subject/body carry the client's own PO number, never ours. */}
      {id !== NIL && (
        <SendDocumentButton
          documentType="PurchaseOrder"
          documentId={id}
          documentLabel={clientPo ? `PO ${clientPo}` : undefined}
          hideTrigger
          noAttachment
          recipientAudience="customer"
          autoOpenSignal={clientSendSignal}
          defaultTo={clientConfirmTo}
          modalTitle="Notify client — order placed"
          defaultSubject={`Your order has been placed${clientPo ? ` — your PO ${clientPo}` : ''}`}
          defaultMessage={`Hi there,\n\nGood news — your order${clientPo ? ` (your PO ${clientPo})` : ''} has been placed with our supplier and is in processing. We'll keep you posted as it progresses.\n\nThank you!`}
        />
      )}
    </>
  );
};

export default withAuthenticationRequired(PurchaseOrderForm);
